// import FormField to handle MPAN input changes and form submission.
import useMpanSearch from "../../hooks/useMpanSearch";
import ErrorBoundary from "../layout/ErrorBoundary";
import FormField from "../layout/FormField";

// Import the MeterDetailsView component to display meter details when a single MPAN is rendered.
import MeterDetailsView from "./MeterDetailsView";
// Import the MultiMpanTable component to display meter details in a table format when multiple MPANs are rendered.
import MultiMpanTable from "./MultiMpanTable";

const MpanSearch: React.FC = () => {
  const {
    mpanInput,
    setMpanInput,
    validationError,
    mpanError,
    isLoading,
    mpanResults,
    handleSearch,
  } = useMpanSearch();

  // Filter out failed results so only successful meter details are passed to the MultiMpanTable component.
  const successfulResults = mpanResults.filter((result) => result.success);

  return (
    <>
      <div className="search-form">
        {/* Render the form input field to allow the user to enter MPAN/s and trigger a search on submit. */}
        <FormField
          inputFields={[
            {
              id: "mpan",
              placeholder: "Enter MPAN/s (comma-separated for multiple MPANs)",
              value: mpanInput,
              onChange: setMpanInput,
            },
          ]}
          onSubmit={handleSearch}
          disabled={isLoading}
          testIdPrefix="electralink-mpan"
        />

        {/* In case of an error, display the error message on the UI. */}
        {validationError && (
          <div className="error-message" data-testid="electralink-mpan-validation-error">
            {validationError}
          </div>
        )}

        {/* Display API errors */}
        {mpanError && (
          <div className="error-message" data-testid="electralink-mpan-api-error">
            {mpanError}
          </div>
        )}

        {/* Display a 'loading' indicator to provide UI feedback to users as they await the API response. */}
        {isLoading && (
          <div className="electralink-loading-message" data-testid="electralink-mpan-loading">
            Please wait... Searches of more than 10 MPANs may take up to one minute.
          </div>
        )}
      </div>
      {/* Display error messages for MPANs that failed to return meter details. */}
      {mpanResults.length > 0 && mpanResults.some((result) => !result.success) && (
        <div className="search-form">
          <div className="error-message" data-testid="electralink-mpan-failed-results">
            {mpanResults
              .filter((result) => !result.success)
              .map(
                (result) => `${result.mpan}: ${result.error ?? "Failed to retrieve meter details"}`,
              )
              .join(", ")}
          </div>
        </div>
      )}

      {/* If the results can't be rendered (e.g. unexpected data from the API), show a message in their place and keep the search form usable. A new search clears the error. */}
      <ErrorBoundary level="section" label="these results" resetKey={mpanResults}>
        {/* Render results for a single MPAN search - hide during loading to prevent stale UI */}
        {!isLoading &&
          mpanResults.length === 1 &&
          mpanResults[0].success &&
          mpanResults[0].meterDetails && (
            <MeterDetailsView meterDetails={mpanResults[0].meterDetails} />
          )}

        {/* Show MultiMpanTable for 2+ MPANs to display TAQ calculations for any HH MPANs. */}
        {!isLoading && mpanResults.length > 1 && successfulResults.length > 0 && (
          <MultiMpanTable results={successfulResults} />
        )}
      </ErrorBoundary>
    </>
  );
};

export default MpanSearch;
